/**
 * commerce-analytics.ts
 *
 * Web-emission layer for mobile commerce GA4 events (MN-6232).
 * Bridges JS → React Native WebView via window.mobileApp.publishTracked.
 *
 * Consent gate (fail-closed): only emit when iubenda explicitly grants
 * s === true && adv === true. When _iub is absent or unloaded, we treat
 * it as NOT consented — this is the deliberate under-reporting tradeoff
 * documented in decision D11-A: a device with no consent API loaded is
 * assumed non-consented, which means we may miss some consented users
 * during iubenda initialisation, but we never fire on a non-consented user.
 *
 * Buffer / transport internals live in commerce-analytics-buffer.ts.
 */

import { emitFirebaseEvent, hasStatisticsConsent, __testBuffer } from "./commerce-analytics-buffer";

export { emitFirebaseEvent };

// ─── Types ────────────────────────────────────────────────────────────────────

export type Ga4EventName =
  | "view_item"
  | "view_item_list"
  | "add_to_cart"
  | "begin_checkout"
  | "add_shipping_info"
  | "add_payment_info"
  | "login"
  | "sign_up";

export interface Ga4Item {
  item_id: string;
  item_name: string;
  item_brand: string;
  item_category?: string;
  item_variant?: string;
  price: number;
  quantity: number;
}

// Raw item shape accepted by the public API. Keys may arrive in EITHER case:
//  - snake_case: JS call sites (PDP/collections) that mirror the GA4 dataLayer shape
//  - camelCase: server `ga4_items` after the checkout config passes through
//    objNamesToCamelCase (App.tsx) — that transform recursively camelizes nested keys,
//    so `item_id` becomes `itemId` by the time it reaches here.
// normalizeItems reads both. price/quantity may come in as strings.
export interface RawGa4Item {
  item_id?: string | number;
  itemId?: string | number;
  item_name?: string | number;
  itemName?: string | number;
  item_brand?: string | number;
  itemBrand?: string | number;
  item_category?: string;
  itemCategory?: string;
  item_variant?: string;
  itemVariant?: string;
  price?: number | string;
  quantity?: number | string;
}

// ─── Item normalization ───────────────────────────────────────────────────────

/**
 * Coerce a raw price/quantity value to a number. undefined/null/NaN → 0.
 */
export function toNumber(v: number | string | undefined | null): number {
  const n = Number(v);
  return Number.isNaN(n) ? 0 : n;
}

const str = (v: unknown): string => (v === undefined || v === null ? "" : String(v));

export function normalizeItems(raw: RawGa4Item[]): Ga4Item[] {
  return (raw ?? []).map(r => {
    const category = r.item_category ?? r.itemCategory;
    const variant = r.item_variant ?? r.itemVariant;
    const item: Ga4Item = {
      item_id: str(r.item_id ?? r.itemId),
      item_name: str(r.item_name ?? r.itemName),
      item_brand: str(r.item_brand ?? r.itemBrand),
      price: toNumber(r.price),
      quantity: r.quantity !== undefined ? toNumber(r.quantity) || 1 : 1
    };
    if (category) item.item_category = category;
    if (variant) item.item_variant = variant;
    return item;
  });
}

// ─── Dedupe / once ───────────────────────────────────────────────────────────

/**
 * Tracks which dedupe keys have already fired.
 * Module lifetime = once per page load; does NOT reset on SPA in-session navigation.
 */
const fired = new Set<string>();

function once(key: string): boolean {
  if (fired.has(key)) return false;
  fired.add(key);
  return true;
}

// ─── Shared ecommerce params builder ─────────────────────────────────────────

function ecommerceParams(
  items: RawGa4Item[],
  value: number | string,
  extra: Record<string, unknown> = {}
): Record<string, unknown> {
  return {
    currency: "USD",
    value: toNumber(value),
    items: normalizeItems(items),
    ...extra
  };
}

// ─── Typed track functions ────────────────────────────────────────────────────

export function trackViewItem(items: RawGa4Item[], value: number | string): void {
  emitFirebaseEvent("view_item", ecommerceParams(items, value));
}

export function trackViewItemList(
  listMeta: { item_list_id?: string; item_list_name?: string },
  items: RawGa4Item[]
): void {
  // Falls back to window.location.pathname when both item_list_id and item_list_name
  // are absent — this collapses all lists sharing a URL into one dedupe key;
  // callers sharing a URL must pass item_list_id/item_list_name to fire independently.
  const key =
    listMeta.item_list_id ||
    listMeta.item_list_name ||
    (typeof window !== "undefined" ? window.location.pathname : "");
  if (!once(`view_item_list:${key}`)) return;
  emitFirebaseEvent("view_item_list", {
    ...listMeta,
    items: normalizeItems(items)
  });
}

export function trackAddToCart(items: RawGa4Item[], value: number | string): void {
  emitFirebaseEvent("add_to_cart", ecommerceParams(items, value));
}

export function trackBeginCheckout(
  items: RawGa4Item[],
  value: number | string,
  dedupeKey: string
): void {
  if (!once(`begin_checkout:${dedupeKey}`)) return;
  emitFirebaseEvent("begin_checkout", ecommerceParams(items, value));
}

export function trackAddShippingInfo(
  items: RawGa4Item[],
  value: number | string,
  opts: { shippingTier?: string; dedupeKey?: string } = {}
): void {
  const { shippingTier, dedupeKey } = opts;
  // GA4 add_shipping_info should fire once per checkout, but the shipping address can
  // be edited/confirmed multiple times. Dedupe on the order so re-edits don't re-fire.
  if (dedupeKey && !once(`add_shipping_info:${dedupeKey}`)) return;
  const extra: Record<string, unknown> = {};
  if (shippingTier) extra["shipping_tier"] = shippingTier;
  emitFirebaseEvent("add_shipping_info", ecommerceParams(items, value, extra));
}

export function trackAddPaymentInfo(
  items: RawGa4Item[],
  value: number | string,
  opts: { paymentType: string; dedupeKey?: string }
): void {
  const { paymentType, dedupeKey } = opts;
  if (dedupeKey) {
    if (!once(`add_payment_info:${dedupeKey}:${paymentType}`)) return;
  }
  emitFirebaseEvent(
    "add_payment_info",
    ecommerceParams(items, value, { payment_type: paymentType })
  );
}

// NOTE: GA4's `method` param (auth provider) is intentionally NOT forwarded. The shell's
// flat-destructure bridge contract (`{ method, name, ...payload }`) reserves `method` for
// routing (`method: 'logEvent'`), so a GA4 `method` param would collide and the event would
// be misrouted/dropped. The event still fires (count intact); the method dimension is
// omitted pending a shell change that supports nested params. `method` arg is retained for
// call-site stability and that future fix. See MN-6232 review notes.
export function trackLogin(_method: string): void {
  emitFirebaseEvent("login", {});
}

export function trackSignUp(_method: string): void {
  emitFirebaseEvent("sign_up", {});
}

// ─── Test helpers (exported for unit tests only) ──────────────────────────────

export const __test = {
  hasStatisticsConsent,
  normalizeItems,
  reset() {
    __testBuffer.reset();
    fired.clear();
  }
};
